export const GOOGLE_SCOPES = Object.freeze({
  openid: "openid",
  email: "email",
  driveReadonly: "https://www.googleapis.com/auth/drive.readonly",
  calendarReadonly: "https://www.googleapis.com/auth/calendar.readonly",
  gmailReadonly: "https://www.googleapis.com/auth/gmail.readonly",
  meetReadonly: "https://www.googleapis.com/auth/meetings.space.readonly"
});

export function googlePreviewUrl(file = {}) {
  if (file.previewUrl) return file.previewUrl;
  const id = encodeURIComponent(String(file.id || file.externalId || ""));
  const mime = String(file.mimeType || "");
  if (!id) return String(file.webViewLink || file.url || "");
  if (mime === "application/vnd.google-apps.document") return `https://docs.google.com/document/d/${id}/preview`;
  if (mime === "application/vnd.google-apps.spreadsheet") return `https://docs.google.com/spreadsheets/d/${id}/preview`;
  if (mime === "application/vnd.google-apps.presentation") return `https://docs.google.com/presentation/d/${id}/preview`;
  if (mime === "application/vnd.google-apps.folder") return String(file.webViewLink || "");
  return `https://drive.google.com/file/d/${id}/preview`;
}

export function googleFileKind(mimeType = "", provider = "") {
  if (provider === "google_gmail" || mimeType === "message/rfc822") return "mail";
  if (provider === "google_calendar" || mimeType === "application/vnd.google-apps.calendar-event") return "calendar";
  if (provider === "google_meet" || mimeType === "application/vnd.google-apps.meet") return "meet";
  if (mimeType === "application/vnd.google-apps.folder") return "folder";
  if (mimeType === "application/vnd.google-apps.document") return "doc";
  if (mimeType === "application/vnd.google-apps.spreadsheet") return "sheet";
  if (mimeType === "application/vnd.google-apps.presentation") return "slide";
  if (mimeType === "application/pdf") return "pdf";
  if (String(mimeType).startsWith("image/")) return "image";
  return "file";
}

export function googleGmailUrl(message = {}) {
  const threadId = encodeURIComponent(String(message.threadId || message.thread_id || ""));
  const messageId = encodeURIComponent(String(message.id || message.externalId || ""));
  const id = threadId || messageId;
  return id ? `https://mail.google.com/mail/u/0/#all/${id}` : "https://mail.google.com/";
}

export class GoogleWorkspaceService {
  constructor(platform) {
    this.platform = platform;
    this.client = platform.client;
    this.workspaceId = platform.workspaceId;
  }

  async invoke(action, payload = {}) {
    if (!this.client) throw new Error("Supabase 연결이 필요합니다.");
    const { data, error } = await this.client.functions.invoke("google-workspace", {
      body: { action, workspaceId: this.workspaceId, ...payload }
    });
    if (error) {
      let message = error.message || "Google Workspace 요청 실패";
      try {
        const details = await error.context?.json?.();
        message = details?.error || message;
      } catch { /* ignore */ }
      throw new Error(message);
    }
    if (data?.error) throw new Error(data.error);
    return data || {};
  }

  async binary(action, payload = {}) {
    const supabaseUrl = String(this.platform.config?.supabaseUrl || "").replace(/\/+$/, "");
    if (!supabaseUrl || !this.client) throw new Error("Supabase 연결이 필요합니다.");
    const { data: sessionData } = await this.client.auth.getSession();
    const token = sessionData?.session?.access_token || this.platform.session?.access_token || "";
    if (!token) throw new Error("AUTH_REQUIRED");
    const response = await fetch(`${supabaseUrl}/functions/v1/google-workspace`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "apikey": String(this.platform.config?.supabaseKey || ""),
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ action, workspaceId: this.workspaceId, ...payload })
    });
    if (!response.ok) {
      let message = `Google Workspace 요청 실패 (${response.status})`;
      try {
        const data = await response.json();
        message = data?.error || message;
        if (response.status === 415 && data?.previewUrl) {
          return {
            blob: null,
            contentType: data?.mimeType || "application/octet-stream",
            filename: "preview",
            fallbackPreviewUrl: data.previewUrl,
            fallbackReason: message,
            resolvedFileId: data?.resolvedFileId || ""
          };
        }
      } catch { /* ignore */ }
      throw new Error(message);
    }
    return {
      blob: await response.blob(),
      contentType: response.headers.get("content-type") || "application/octet-stream",
      filename: decodeURIComponent(response.headers.get("x-naysor-filename") || "preview"),
      resolvedFileId: decodeURIComponent(response.headers.get("x-naysor-resolved-file-id") || ""),
      resolvedMimeType: decodeURIComponent(response.headers.get("x-naysor-resolved-mime-type") || "")
    };
  }

  status() { return this.invoke("status"); }
  connect(returnTo = window.location.href) { return this.invoke("start", { returnTo }); }
  disconnect() { return this.invoke("disconnect"); }
  sharedDrives() { return this.invoke("shared-drives"); }
  files(options = {}) { return this.invoke("files", options); }
  fileDetails(fileId, mimeType = "", attachmentId = "") { return this.invoke("file-details", { fileId, mimeType, attachmentId }); }
  previewTicket(fileId, mimeType = "", attachmentId = "") { return this.invoke("preview-ticket", { fileId, mimeType, attachmentId }); }
  previewFile(fileId, mimeType = "", attachmentId = "") { return this.binary("file-preview", { fileId, mimeType, attachmentId }); }
  thumbnailFile(fileId, attachmentId = "") { return this.binary("file-thumbnail", { fileId, attachmentId }); }
  nativePreview(fileId, mimeType = "", attachmentId = "") { return this.invoke("native-preview", { fileId, mimeType, attachmentId }); }
  slideThumbnail(fileId, pageObjectId, attachmentId = "") { return this.invoke("slide-thumbnail", { fileId, pageObjectId, attachmentId }); }
  slideThumbnails(fileId, pageObjectIds = [], attachmentId = "") { return this.invoke("slide-thumbnails", { fileId, pageObjectIds, attachmentId }); }
  calendars() { return this.invoke("calendars"); }
  calendarEvents(options = {}) { return this.invoke("calendar-events", options); }
  calendarEventsBatch(options = {}) { return this.invoke("calendar-events-batch", options); }
  gmailSearch(query = "in:inbox newer_than:90d", maxResults = 25) { return this.invoke("gmail-search", { query, maxResults }); }
  gmailMessage(messageId, attachmentId = "") { return this.invoke("gmail-message", { messageId, attachmentId }); }
  captureSnapshot(attachmentId) { return this.invoke("capture-snapshot", { attachmentId }); }
  meetRecords(maxResults = 20) { return this.invoke("meet-records", { maxResults }); }
}
